import { Box, Tooltip, Typography } from "@mui/material";
import { COLORS, MOTION_MS, fmtCount, fmtPct, reducedMotion } from "./emsTheme";

/**
 * Sorted horizontal bar chart. Bars start at zero on one shared scale, largest first. One bar can be
 * the focus (orange) with the rest in context grey; otherwise every bar is the single-series purple.
 * Each bar is a button (keyboard reachable) that opens the ticket list for that slice.
 *
 * rows: [{ key, label, value, denominator?, n? }]
 * unit: "count" | "pct" (pct shows value / denominator). formatValue overrides the value text, e.g. durations.
 */
const SortedBarChart = ({ rows, unit = "count", focusKey, focusTop = false, denominatorLabel = "tickets", maxRows = 10, onSelect, ariaLabel, formatValue }) => {
    const motion = !reducedMotion();
    const shown = [...(rows || [])]
        .map((r) => ({ ...r, display: unit === "pct" ? (r.denominator ? (r.value * 100) / r.denominator : 0) : r.value }))
        .sort((a, b) => b.display - a.display);
    const top = shown.slice(0, maxRows);
    const rest = shown.slice(maxRows);
    if (rest.length) {
        const value = rest.reduce((a, r) => a + r.value, 0);
        const denominator = rest.reduce((a, r) => a + (r.denominator || 0), 0);
        top.push({ key: "__others", label: `Others (${rest.length})`, value, denominator, display: unit === "pct" ? (denominator ? (value * 100) / denominator : 0) : value, others: true });
    }
    const max = Math.max(1, ...top.map((r) => r.display));
    // Only a bar with something in it can be the focus.
    const focus = focusKey !== undefined ? focusKey : (focusTop && top.length && top[0].display > 0 ? top[0].key : undefined);

    return (
        <Box component="ul" aria-label={ariaLabel} sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1 }}>
            {top.map((r) => {
                const isFocus = focus !== undefined && r.key === focus;
                const color = focus === undefined ? (focusTop ? COLORS.context : COLORS.purple) : (isFocus ? COLORS.orange : COLORS.context);
                const valueText = formatValue ? formatValue(r.value) : unit === "pct" ? fmtPct(r.display) : fmtCount(r.value);
                const tip = formatValue ? valueText : r.denominator != null
                    ? `${unit === "pct" ? fmtPct(r.display) : fmtCount(r.value)} · ${fmtCount(r.value)} of ${fmtCount(r.denominator)} ${denominatorLabel}`
                    : `${fmtCount(r.value)} ${denominatorLabel}`;
                const clickable = onSelect && !r.others && r.key != null;
                return (
                    <Box component="li" key={r.key ?? r.label} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(96px, 32%) 1fr" }, alignItems: "center", gap: { xs: 0.25, sm: 1.5 } }}>
                        <Typography title={r.label} sx={{ fontSize: 13, color: COLORS.ink2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isFocus ? 600 : 400 }}>
                            {r.label}
                        </Typography>
                        <Tooltip title={tip} placement="top" arrow>
                            <Box component={clickable ? "button" : "div"} type={clickable ? "button" : undefined}
                                onClick={clickable ? () => onSelect(r) : undefined}
                                aria-label={`${r.label}: ${tip}${clickable ? ". Open these tickets" : ""}`}
                                sx={{ all: "unset", display: "flex", alignItems: "center", gap: 1, cursor: clickable ? "pointer" : "default", borderRadius: "4px",
                                    "&:focus-visible": { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 },
                                    "&:hover .bar": clickable ? { filter: "brightness(0.92)" } : {} }}>
                                <Box sx={{ flex: 1, height: 18, bgcolor: "#F3F4F6", borderRadius: "3px", overflow: "hidden" }}>
                                    <Box className="bar" sx={{ height: "100%", width: `${(r.display / max) * 100}%`, minWidth: r.display > 0 ? 3 : 0, bgcolor: color, borderRadius: "3px",
                                        transition: motion ? `width ${MOTION_MS}ms ease-out` : "none" }} />
                                </Box>
                                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: isFocus ? COLORS.orangeText : COLORS.ink, minWidth: 52, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {valueText}
                                    {/* A percentage alone hides how small the base is (8 of 8 reads as 100%), so show the counts with it. */}
                                    {unit === "pct" && !formatValue && r.denominator != null && (
                                        <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: COLORS.ink3, ml: 0.75 }}>
                                            {fmtCount(r.value)} of {fmtCount(r.denominator)}
                                        </Box>
                                    )}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                );
            })}
        </Box>
    );
};

export default SortedBarChart;
